import { useEffect, useRef } from "react";

import {
	useProductStore,
	useProductStoreActions,
} from "@/modules/product/stores/product.store";
import type { TProducts } from "@/modules/product/types/product.type";
import { useAxiosHttpClient } from "@/modules/shared/infra/axios-http-client.infra";
import { HttpStatusCode } from "@/modules/shared/protocols/http-client.protocol";
import { useOrderFormStore } from "../../stores/order-form.store";
import { useOrderFormComponentStore, useOrderFormComponentStoreActions } from "../../stores/order-form-component.store";
import { orderFormPaginateProductService, type TOrderFormPaginateProductService } from "../../services/order-form-paginate-product.service";
import { orderFormProductService, type TOrderFormProductService } from "../../services/order-form-product.service";

export const useOrderFormCatalogData = () => {
	const { jsonOrderForm } = useOrderFormStore();

	const { filterToCreatePaginationOfProduct: filter } =
		useOrderFormComponentStore();
	const { setFilterToCreatePaginationOfProduct: setFilter } =
		useOrderFormComponentStoreActions;

	const { productPaginate, products, isActiveInfinityScroll } =
		useProductStore();
	const { setProductPaginate, setProducts, setIsActiveInfinityScroll } =
		useProductStoreActions;

	const { axiosRequest: paginateProductRequest } = useAxiosHttpClient({
		sendRequest: orderFormPaginateProductService,
	});

	const { axiosRequest: productRequest } = useAxiosHttpClient({
		sendRequest: orderFormProductService,
	});

	// Ref para armazenar o filtro atual da requisição
	const currentFilterRef = useRef<string>("");
	// Ref para evitar POST duplicado com o mesmo payload
	const lastProductRequestKeyRef = useRef<string>("");

	const handleSubmitPaginateProduct = ({
		payload,
	}: TOrderFormPaginateProductService) => {
		// Verificar se já está carregando ou se a requisição é desnecessária
		if (paginateProductRequest.isPending || productRequest.isPending) {
			return; // Evitar requisições concorrentes
		}

		// Garantir que o filtro nunca seja undefined
		const validFilter = payload.filter || "produto.descricao";

		// Atualizar o ref com o filtro atual
		currentFilterRef.current = validFilter;

		// Atualizar o payload com o filtro válido
		const validPayload = {
			...payload,
			filter: validFilter,
		};

		paginateProductRequest.mutate({
			showInfo: true,
			payload: validPayload,
		});

		setFilter(validFilter);
	};

	const handleSubmitProduct = async ({ payload }: TOrderFormProductService) => {

		if (!productRequest.isPending) {
			// Garantir que o filtro nunca seja undefined
			const validPayload = {
				...payload,
				filter:
					payload.filter || currentFilterRef.current || "produto.descricao",
			};

			// Monta chave única para deduplicar chamadas iguais
			const requestKey = `${validPayload.filter}|${validPayload.arrayOfProducts}`;

			if (lastProductRequestKeyRef.current === requestKey) {
				return; // Evita POST duplicado ao mesmo endpoint com mesmo payload
			}

			lastProductRequestKeyRef.current = requestKey;

			await productRequest.mutateAsync({ payload: validPayload });
		}
	};

	useEffect(() => {
		if (paginateProductRequest.isSuccess) {
			if (paginateProductRequest.data.status === HttpStatusCode.OK) {
				const responseParsed = JSON.parse(paginateProductRequest.data.body);

				setProductPaginate(responseParsed);

				handleSubmitProduct({
					payload: {
						arrayOfProducts: responseParsed?.pagina[0],
						jsonOrderForm,
						filter: currentFilterRef.current || "produto.descricao",
					},
				});
				return;
			}

			if (paginateProductRequest.data.status === 204) {
				setProducts([]);
				setProductPaginate(null);
				setIsActiveInfinityScroll(false);
				return;
			}

			setIsActiveInfinityScroll(false);
		}
	}, [paginateProductRequest.isSuccess]);
	useEffect(() => {

		if (productRequest.isSuccess) {
			if (productRequest.data.status === HttpStatusCode.OK) {
				const responseParsed: TProducts[] = JSON.parse(
					productRequest.data.body,
				);

				if (isActiveInfinityScroll && productPaginate?.totalPaginas > 1) {
					const mergedProducts = [...products];
					for (const newProd of responseParsed) {
						const index = mergedProducts.findIndex(
							(existingProd) => existingProd.fkProduto === newProd.fkProduto,
						);

						if (index !== -1) {
							mergedProducts[index] = newProd;
						} else {
							mergedProducts.push(newProd);
						}
					}

					setProducts(mergedProducts);
					setIsActiveInfinityScroll(false);
					return;
				}

				setProducts(responseParsed);
			}

			setIsActiveInfinityScroll(false);
		}
	}, [productRequest.isSuccess]);

	return {
		isLoadingRequestProduct:
			productRequest.isLoading || paginateProductRequest.isLoading,
		handleSubmitProduct,
		handleSubmitPaginateProduct,
	};
};
